import produce from "immer";
import _ from "lodash";
import { Action } from "redux";
import { INode } from "../search/types";
import {
  INodeTree,
  ISelectedInstallation,
  ITemperatureHistory,
  IInstallationExtended,
  ISetInstallations,
  IBillingResult,
} from "./types";

export interface INodeState {
  currentTemperatureForNode: number | undefined;
  currentRelativeHumidityForNode: number | undefined;
  isFetching: boolean;
  selectedNode: INode | undefined;
  temperatureHistory: ITemperatureHistory[];
  nodeTree: INodeTree;
  installations: IInstallationExtended[] | undefined;
  selectedInstallation: ISelectedInstallation | null;
  currentMonthCost: number;
  currentMonthEmission: number;
  error: boolean;
  billingResults: IBillingResult[];
  isFetchingBillingResults: boolean;
  fetchBillingResultsFailed: boolean;
}

const initialState: INodeState = {
  currentTemperatureForNode: undefined,
  currentRelativeHumidityForNode: undefined,
  temperatureHistory: [],
  isFetching: false,
  selectedNode: undefined,
  nodeTree: {
    ParentNodes: [],
    ParentNodeID: "",
    ID: 0,
    Name: "",
    NodeType: { ID: 0, Code: 0, Name: "" },
  },
  installations: undefined,
  selectedInstallation: null,
  currentMonthCost: 0,
  currentMonthEmission: 0,
  error: false,
  billingResults: [],
  isFetchingBillingResults: false,
  fetchBillingResultsFailed: false,
};

const FETCHING = "NODEDETAILS/FETCHING";
const FETCHING_TEMP_ERROR = "NODEDETAILS/FETCHING_TEMP_ERROR";
const SET_SELECTED_NODE = "NODEDETAILS/SET_SELECTED_NODE";
const FETCH_CURRENT_TEMP_SUCCESS = "NODEDETAILS/FETCH_CURRENT_TEMP_SUCCESS";
const FETCH_CURRENT_RELATIVEHUMIDITY = "NODEDETAILS/FETCH_RELATIVE_HUMIDITY";
const FETCH_NODE_TREE = "NODEDETAILS/FETCH_NODE_TREE";
const CLEAR_CURRENT_RELATIVE_HUMIDITY_AND_TEMPERATURE =
  "NODEDETAILS/CLEAR_CURRENT_RELATIVE_HUMIDITY_AND_TEMPERATURE";
const CLEAR_TEMPERATURE_HISTORY = "NODEDETAILS/CLEAR_TEMPERATURE_HISTORY";
const FETCH_INSTALLATIONS_SUCCESS = "NODEDETAILS/FETCH_INSTALLATIONS_SUCCESS";
const FETCH_INSTALLATIONS_ERROR = "NODEDETAILS/FETCH_INSTALLATIONS_ERROR";
const RESET_SELECTED_INSTALLATION = "NODEDETAILS/RESET_SELECTED_INSTALLATION";
const FETCH_CURRENT_MONTH_COST_SUCCESS = "NODEDETAILS/FETCH_TOTAL_COST";
const FETCH_CURRENT_MONTH_EMISSION_SUCCESS = "NODEDETAILS/FETCH_TOTAL_EMISSION";
const SET_SELECTED_INSTALLATION_SUCCESS =
  "NODEDETAILS/SET_SELECTED_INSTALLATION_SUCCESS";
const SET_BILLING_RESULTS = "NODEDETAILS/SET_BILLING_RESULTS";
const SET_IS_FETCHING_BILLING_RESULTS =
  "NODEDETAILS/SET_IS_FETCHING_BILLING_RESULTS";
const SET_FETCH_BILLING_RESULTS_FAILED =
  "NODEDETAILS/SET_FETCH_BILLING_RESULTS_FAILED";

export interface ISetSelectedInstallationSuccessAction {
  type: typeof SET_SELECTED_INSTALLATION_SUCCESS;
}

export interface IFetchingAction {
  type: typeof FETCHING;
}
export interface IClearCurrentRelativeHumidityAndTemperatureAction {
  type: typeof CLEAR_CURRENT_RELATIVE_HUMIDITY_AND_TEMPERATURE;
}
export interface ISetInstallationsAction extends Action {
  type: typeof FETCH_INSTALLATIONS_SUCCESS;
  payload: ISetInstallations;
}
export interface ISetInstallationsErrorAction extends Action {
  type: typeof FETCH_INSTALLATIONS_ERROR;
}
export interface IFetchingTemperatureErrorAction {
  type: typeof FETCHING_TEMP_ERROR;
}
export interface ISetSelectedNodeAction {
  type: typeof SET_SELECTED_NODE;
  payload: INode;
}
export interface IFetchCurrentTempSuccessAction {
  type: typeof FETCH_CURRENT_TEMP_SUCCESS;
  payload: { results: number };
}
export interface IFetchCurrentRelativeHumidityAction {
  type: typeof FETCH_CURRENT_RELATIVEHUMIDITY;
  payload: { results: number };
}
export interface IFetchNodeTreeAction {
  type: typeof FETCH_NODE_TREE;
  payload: INodeTree;
}
export interface IClearTemperatureHistoryAction {
  type: typeof CLEAR_TEMPERATURE_HISTORY;
}
export interface ISetSelectedInstallationSuccessAction {
  type: typeof SET_SELECTED_INSTALLATION_SUCCESS;
  payload: ISelectedInstallation;
}
export interface IResetSelectedInstallationAction {
  type: typeof RESET_SELECTED_INSTALLATION;
}
export interface IFetchCurrentMonthCostSuccessAction {
  payload: number;
  type: typeof FETCH_CURRENT_MONTH_COST_SUCCESS;
}
export interface IFetchCurrentMonthEmissionSuccessAction {
  payload: number;
  type: typeof FETCH_CURRENT_MONTH_EMISSION_SUCCESS;
}
export interface ISetBillingResultsAction {
  type: typeof SET_BILLING_RESULTS;
  payload: IBillingResult[];
}
export interface ISetIsFetchingBillingResultsAction {
  type: typeof SET_IS_FETCHING_BILLING_RESULTS;
  payload: boolean;
}
export interface ISetFetchBillingResultsFailedAction {
  type: typeof SET_FETCH_BILLING_RESULTS_FAILED;
  payload: boolean;
}
export const actionCreator = {
  fetching: { type: FETCHING } as IFetchingAction,
  clearCurrentRelativeHumidityAndTemperature: {
    type: CLEAR_CURRENT_RELATIVE_HUMIDITY_AND_TEMPERATURE,
  } as IClearCurrentRelativeHumidityAndTemperatureAction,
  fetchingTemperatureError: {
    type: FETCHING_TEMP_ERROR,
  } as IFetchingTemperatureErrorAction,
  setSelectedNode: (node: INode) =>
    ({ type: SET_SELECTED_NODE, payload: node } as ISetSelectedNodeAction),
  fetchingCurrentTemp: (results: number) =>
    ({
      type: FETCH_CURRENT_TEMP_SUCCESS,
      payload: { results },
    } as IFetchCurrentTempSuccessAction),
  fetchingCurrentRelativeHumidity: (results: number) =>
    ({
      type: FETCH_CURRENT_RELATIVEHUMIDITY,
      payload: { results },
    } as IFetchCurrentRelativeHumidityAction),

  setNodeTree: (nodeTree: INodeTree) =>
    ({
      type: FETCH_NODE_TREE,
      payload: nodeTree,
    } as IFetchNodeTreeAction),

  clearTemperatureHistory: () =>
    ({
      type: CLEAR_TEMPERATURE_HISTORY,
    } as IClearTemperatureHistoryAction),
  setInstallations: (payload: ISetInstallations) =>
    ({
      type: FETCH_INSTALLATIONS_SUCCESS,
      payload,
    } as ISetInstallationsAction),
  setInstallationsError: {
    type: FETCH_INSTALLATIONS_ERROR,
  } as ISetInstallationsErrorAction,
  setSelectedInstallation: (payload: ISelectedInstallation) =>
    ({
      type: SET_SELECTED_INSTALLATION_SUCCESS,
      payload: payload,
    } as ISetSelectedInstallationSuccessAction),
  resetSelectedInstallation: {
    type: RESET_SELECTED_INSTALLATION,
  },
  fetchingCurrentMonthCost: (payload: number) =>
    ({
      type: FETCH_CURRENT_MONTH_COST_SUCCESS,
      payload: payload,
    } as IFetchCurrentMonthCostSuccessAction),
  fetchingCurrentMonthEmission: (payload: number) =>
    ({
      type: FETCH_CURRENT_MONTH_EMISSION_SUCCESS,
      payload: payload,
    } as IFetchCurrentMonthEmissionSuccessAction),
  setFetchingBillingResults: (isFetching: boolean) =>
    ({
      type: SET_IS_FETCHING_BILLING_RESULTS,
      payload: isFetching,
    } as ISetIsFetchingBillingResultsAction),
  setFetchBillingResultsFailed: (failed: boolean) =>
    ({
      type: SET_FETCH_BILLING_RESULTS_FAILED,
      payload: failed,
    } as ISetFetchBillingResultsFailedAction),
  setBillingResults: (billingResult: IBillingResult[]) =>
    ({
      type: SET_BILLING_RESULTS,
      payload: billingResult,
    } as ISetBillingResultsAction),
};

export const nodeDetailsReducer = (
  state = initialState,
  action: Action<string>
) =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCHING:
        draft.isFetching = true;
        break;
      case FETCH_CURRENT_TEMP_SUCCESS:
        const fetchingCurrentTempAction =
          action as IFetchCurrentTempSuccessAction;
        draft.currentTemperatureForNode =
          fetchingCurrentTempAction.payload.results;
        break;
      case FETCH_CURRENT_RELATIVEHUMIDITY:
        const fetchingCurrentRelativeHumidityAction =
          action as IFetchCurrentRelativeHumidityAction;
        draft.currentRelativeHumidityForNode =
          fetchingCurrentRelativeHumidityAction.payload.results;
        break;
      case FETCHING_TEMP_ERROR:
        draft.isFetching = false;
        draft.temperatureHistory = initialState.temperatureHistory;
        break;
      case CLEAR_CURRENT_RELATIVE_HUMIDITY_AND_TEMPERATURE:
        draft.currentRelativeHumidityForNode = undefined;
        draft.currentTemperatureForNode = undefined;
        break;
      case SET_SELECTED_NODE:
        const setSelectedNodeAction = action as ISetSelectedNodeAction;
        draft.selectedNode = setSelectedNodeAction.payload;
        break;
      case RESET_SELECTED_INSTALLATION:
        draft.selectedInstallation = initialState.selectedInstallation;
        break;

      case FETCH_NODE_TREE:
        const fetchNodeTreeAction = action as IFetchNodeTreeAction;
        draft.nodeTree = fetchNodeTreeAction.payload;
        break;
      case CLEAR_TEMPERATURE_HISTORY:
        draft.temperatureHistory = initialState.temperatureHistory;
        break;
      case FETCH_INSTALLATIONS_SUCCESS:
        const registersAction = action as ISetInstallationsAction;
        draft.installations = registersAction.payload.installationsExtended;
        draft.currentMonthCost = registersAction.payload.totalMonthCost;
        draft.currentMonthEmission = registersAction.payload.totalMonthEmission;
        draft.error = false;
        draft.isFetching = false;
        break;
      case FETCH_INSTALLATIONS_ERROR:
        draft.installations = initialState.installations;
        draft.error = true;
        draft.isFetching = false;
        break;
      case SET_SELECTED_INSTALLATION_SUCCESS:
        const setSelectedInstallationSuccessAction =
          action as ISetSelectedInstallationSuccessAction;
        draft.selectedInstallation =
          setSelectedInstallationSuccessAction.payload;
        draft.isFetching = false;
        break;
      case RESET_SELECTED_INSTALLATION:
        draft.selectedInstallation = initialState.selectedInstallation;
        break;
      case SET_BILLING_RESULTS:
        const setBillingResultsAction = action as ISetBillingResultsAction;
        draft.billingResults = setBillingResultsAction.payload;
        break;
      case SET_IS_FETCHING_BILLING_RESULTS:
        const setFetchingBillingResultsAction =
          action as ISetIsFetchingBillingResultsAction;
        draft.isFetchingBillingResults =
          setFetchingBillingResultsAction.payload;
        break;
      case SET_FETCH_BILLING_RESULTS_FAILED:
        const setFetchBillingResultsFailedAction =
          action as ISetFetchBillingResultsFailedAction;
        draft.fetchBillingResultsFailed =
          setFetchBillingResultsFailedAction.payload;
        break;
    }
  });
